import { useTranslation } from 'react-i18next';

const adv01Img = '/images/advantages-modular-architecture.png';

const adv02Img = 'http://geel.fp.08wx.cn/images/696e228806fdf14b6c47888eff2e4e69.jpg';

interface AdvPointsProps {
  points: { icon: string; text: string }[];
  color: string;
}

function AdvPoints({ points, color }: AdvPointsProps) {
  return (
    <div className="space-y-3">
      {points.map((p, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className={`${p.icon} text-sm`} style={{ color }} />
          </div>
          <span className="text-gray-600 text-sm">{p.text}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdvDetailsSection01() {
  const { t } = useTranslation();
  const adv01Points = Array.from({ length: 5 }, (_, i) => ({ icon: 'ri-checkbox-circle-line', text: t(`advantages.s1.p1.${i}`) }));
  const adv02Points = Array.from({ length: 5 }, (_, i) => ({ icon: 'ri-checkbox-circle-line', text: t(`advantages.s1.p2.${i}`) }));

  return (
    <section className="bg-white py-24 space-y-0">
      {/* ADV 01: Modular Architecture */}
      <div className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 xl:gap-20 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full">
              <span className="text-primary text-xs font-mono font-bold">01</span>
              <span className="text-primary text-xs font-medium uppercase tracking-wider">{t('advantages.s1.tag1')}</span>
            </div>

            <h2 className="text-4xl font-heading font-bold text-gray-900 mb-6 leading-tight">
              {t('advantages.s1.title1')}
              <br />
              <span className="text-primary">{t('advantages.s1.title1h')}</span>
            </h2>

            <p className="text-gray-500 text-base leading-relaxed mb-6">
              {t('advantages.s1.desc1')}
              {t('advantages.s1.desc2')}
              {t('advantages.s1.desc3')}
            </p>

            <div className="mb-8">
              <AdvPoints points={adv01Points} color="#FF2D78" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              {[
                { val: '80%', label: t('advantages.s1.stat1') },
                { val: '3x', label: t('advantages.s1.stat2') },
                { val: '60%', label: t('advantages.s1.stat3') },
              ].map((stat, i) => (
                <div key={i} className="bg-pink-50 border border-primary/15 rounded-xl p-4 text-center">
                  <div className="text-2xl font-heading font-black text-primary">{stat.val}</div>
                  <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden border border-primary/15">
            <div className="w-full h-[400px]">
              <img src={adv01Img} alt="Modular Architecture" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
            </div>
            <div className="absolute top-5 left-5 bg-white/95 border border-primary/20 rounded-xl px-4 py-3 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-puzzle-2-line text-primary text-sm" />
                <span className="text-gray-800 text-xs font-medium">{t('advantages.s1.badge1')}</span>
              </div>
              <div className="text-gray-500 text-xs">{t('advantages.s1.badge1d')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-primary/15 to-transparent" />
      </div>

      {/* ADV 02: Full Chain Ecosystem */}
      <div className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 xl:gap-20 items-center">
          <div className="relative rounded-2xl overflow-hidden border border-primary/15 order-2 lg:order-1">
            <div className="w-full h-[400px]">
              <img src={adv02Img} alt="Multi-Chain Ecosystem" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
            </div>
            <div className="absolute top-5 right-5 bg-white/95 border border-primary/25 rounded-xl px-4 py-3 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-links-line text-primary text-sm" />
                <span className="text-gray-800 text-xs font-medium">{t('advantages.s1.badge2')}</span>
              </div>
              <div className="text-gray-500 text-xs">{t('advantages.s1.badge2d')}</div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full">
              <span className="text-primary text-xs font-mono font-bold">02</span>
              <span className="text-primary text-xs font-medium uppercase tracking-wider">{t('advantages.s1.tag2')}</span>
            </div>

            <h2 className="text-4xl font-heading font-bold text-gray-900 mb-6 leading-tight">
              {t('advantages.s1.title2')}
              <br />
              <span className="text-primary">{t('advantages.s1.title2h')}</span>
            </h2>

            <p className="text-gray-500 text-base leading-relaxed mb-6">
              {t('advantages.s1.desc4')}
              {t('advantages.s1.desc5')}
              {t('advantages.s1.desc6')}
            </p>

            <div className="mb-8">
              <AdvPoints points={adv02Points} color="#FF2D78" />
            </div>

            {/* Chain logos */}
            <div className="flex flex-wrap gap-2">
              {['Ethereum', 'BSC', 'Solana', 'Tron', 'Polygon', 'Arbitrum', 'Optimism', 'Avalanche'].map((chain, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 bg-pink-50 border border-primary/20 text-gray-500 text-xs rounded-lg"
                >
                  {chain}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
